import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Bug, Inbox, Lightbulb, MessageSquarePlus, MessagesSquare } from "lucide-react";
import { useCallback } from "react";
import { createPortal } from "react-dom";

import { api } from "../api/client";
import type { FeedbackMine } from "../api/types";
import { Menu, MenuItem } from "../components/Menu";
import { Presence } from "../components/presence";
import { Badge, Button, IconButton } from "../components/ui";
import { t } from "../i18n/i18n";
import { FeedbackAdminDialog } from "./FeedbackAdminDialog";
import { FeedbackDialog } from "./FeedbackDialog";
import {
  closeFeedback,
  countLabel,
  FEEDBACK_KEY,
  type FeedbackPlace,
  openFeedback,
  openFeedbackPane,
  setFeedbackMenu,
  useFeedback,
  waitingCount,
} from "./feedbackModel";
import { MyFeedbackDialog } from "./MyFeedbackDialog";
import { ReportLinks } from "./reportLink";

/** Fenêtres des retours (registre F39), posées une fois à la racine de l'application (accueil compris). */
export function FeedbackHost() {
  const pane = useFeedback((s) => s.pane);
  const capture = useFeedback((s) => s.capture);
  const context = useFeedback((s) => s.context);
  const prefill = useFeedback((s) => s.prefill);
  const form = pane === "evolution" || pane === "anomaly";
  // Au-dessus de l'accueil (z-60), comme le portail des comptes : les fenêtres s'ouvrent aussi depuis l'accueil.
  // Pas de « Signaler cette anomalie » dans les fenêtres des retours elles-mêmes.
  return createPortal(
    <ReportLinks enabled={false}>
      <div className="relative z-[61]">
        <Presence show={form}>
          {form && context && <FeedbackDialog kind={pane} capture={capture} context={context} prefill={prefill} onClose={closeFeedback} />}
        </Presence>
        <Presence show={pane === "mine"}>{pane === "mine" && <MyFeedbackDialog onClose={closeFeedback} />}</Presence>
        <Presence show={pane === "admin"}>{pane === "admin" && <FeedbackAdminDialog onClose={closeFeedback} />}</Presence>
      </div>
    </ReportLinks>,
    document.body,
  );
}

/** « Mes retours » et l'administration : réponses non lues, retours nouveaux (licence d'administration). */
export function useFeedbackSummary(enabled = true, refetchInterval?: number) {
  return useQuery<FeedbackMine>({ queryKey: FEEDBACK_KEY, queryFn: api.feedback.mine, enabled, staleTime: 60_000, refetchInterval });
}

/** Entrées du menu du bouton « Retours », seul chemin vers les retours (décision d'Alexis du 21/10/2026) :
 *  suggérer, signaler, mes retours (réponses non lues en pastille) et, pour une licence d'administration, les
 *  retours de toutes les licences (nouveaux en pastille). */
export function FeedbackMenuItems({ open }: { open: boolean }) {
  const queryClient = useQueryClient();
  const { data } = useFeedbackSummary(open);
  return (
    <>
      <MenuItem icon={Lightbulb} label={t("Suggérer une évolution")} onSelect={() => openFeedback("evolution", queryClient)} />
      <MenuItem icon={Bug} label={t("Signaler une anomalie")} onSelect={() => openFeedback("anomaly", queryClient)} />
      <MenuItem
        icon={Inbox}
        label={t("Mes retours")}
        trailing={data?.unread ? <Badge tone="accent">{data.unread}</Badge> : undefined}
        hint={data?.unread ? t("Nouvelle réponse de l'équipe") : undefined}
        onSelect={() => openFeedbackPane("mine")}
      />
      {data?.admin && (
        <MenuItem
          icon={MessagesSquare}
          label={t("Retours des utilisateurs")}
          trailing={data.admin_new ? <Badge tone="accent">{data.admin_new}</Badge> : undefined}
          onSelect={() => openFeedbackPane("admin")}
        />
      )}
    </>
  );
}

/** Bouton « Retours » (registre F39, demande d'Alexis du 21/10/2026 : le point d'entrée doit se voir) : toujours
 *  visible, libellé, sur toutes les vues — dans l'en-tête de l'accueil (`home`), en bas de la barre latérale de
 *  l'espace de travail (`sidebar`, une ligne au-dessus de la langue), ou en icône dans le rail replié (`rail`,
 *  tablette en portrait comprise). Hors de la carte : aucun contrôle de la carte, panneau flottant, barre d'état ni
 *  avis ne le recouvre. Ouvre le menu « Suggérer une évolution · Signaler une anomalie · Mes retours » (et
 *  l'administration des retours pour une licence d'administration). Pastille : réponses non lues de l'équipe, plus
 *  les nouveaux retours pour l'administration. Absent de l'écran d'activation et de connexion (monté dans `App`). */
export function FeedbackLauncher({ place }: { place: "home" | "sidebar" | "rail" }) {
  const target: FeedbackPlace = place === "home" ? "home" : "sidebar";
  const open = useFeedback((s) => s.menu === target);
  const close = useCallback(() => setFeedbackMenu(null), []);
  const toggle = () => setFeedbackMenu(open ? null : target);
  // Relu toutes les 5 minutes : une réponse de l'équipe se signale sans recharger la page.
  const { data } = useFeedbackSummary(true, 300_000);
  const waiting = waitingCount(data);
  const label = t("Retours");
  const title = waiting
    ? t("Retours : évolutions et anomalies · {n} à lire", { n: countLabel(waiting) })
    : t("Retours : évolutions et anomalies");
  const trigger =
    place === "home" ? (
      <Button variant="ghost" onClick={toggle} title={title} aria-expanded={open} data-feedback-launcher="home">
        <MessageSquarePlus size={14} /> {label}
        {waiting > 0 && (
          <Badge tone="accent" size="sm" className="-mr-0.5">
            {countLabel(waiting)}
          </Badge>
        )}
      </Button>
    ) : place === "rail" ? (
      <span className="relative inline-flex">
        <IconButton
          size="sm"
          onClick={toggle}
          title={title}
          aria-label={title}
          aria-expanded={open}
         
          data-feedback-launcher="rail"
        >
          <MessageSquarePlus size={16} className="text-accent" />
        </IconButton>
        {waiting > 0 && (
          <Badge tone="accent" variant="solid" size="sm" className="pointer-events-none absolute -top-1 -right-1.5 ring-2 ring-surface">
            {countLabel(waiting)}
          </Badge>
        )}
      </span>
    ) : (
      <button
        type="button"
        onClick={toggle}
        title={title}
        aria-expanded={open}
       
        data-feedback-launcher="sidebar"
        className={clsx(
          "flex h-7 w-full items-center gap-2.5 rounded-lg px-3 text-ui text-ink transition-colors hover:bg-hover",
          open && "bg-selected",
        )}
      >
        <MessageSquarePlus size={16} className="shrink-0 text-accent" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left font-medium">{label}</span>
        {waiting > 0 ? (
          <Badge tone="accent" variant="solid">
            {countLabel(waiting)}
          </Badge>
        ) : (
          <span className="shrink-0 truncate text-meta text-ink-muted">{t("Évolution · anomalie")}</span>
        )}
      </button>
    );
  return (
    <Menu
      open={open}
      onClose={close}
      portal
      side={place === "home" ? "bottom" : place === "rail" ? "right" : "top"}
      align={place === "sidebar" ? "start" : "end"}
      width={place === "sidebar" ? 236 : 264}
      ariaLabel={t("Retours : évolutions et anomalies")}
      wrapperClassName={place === "sidebar" ? "flex w-full" : undefined}
      trigger={trigger}
    >
      <FeedbackMenuItems open={open} />
    </Menu>
  );
}
